import { useEffect, useState } from 'react';
import { Table, PageTitleWithButton, Pagination, MobileTable } from '../../../components';
import { useSnackbar } from 'notistack';
import { Box, FormControlLabel, Checkbox, Divider, Button, useMediaQuery } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { Add, Edit } from '@mui/icons-material';
import { getJobsList } from '../../../fetch';
import { IJobList } from '../../../models';
import { JobFilters } from './job-filters';
import { CreateJobModal } from './CreateJobModal';
import { JobSearch } from './job-search';
import { theme } from '../../../styles/theme';

export const JobsList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [jobs, setJobs] = useState<IJobList[]>([]);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string>('');
  const [isLoadingJobs, setLoadingJobs] = useState<boolean>(true);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [isModalOpen, showModal] = useState<boolean>(false);
  const history = useHistory();
  const [filters, setFilters] = useState<{
    neighborhoodId?: number | string;
    jobStatus?: string;
    jobType?: string;
  }>({});
  const [sortDirection, setSortDirection] = useState<{
    neighborhoodDescription?: 'Asc' | 'Desc';
    lotNumber?: 'Asc' | 'Desc';
    jobId?: 'Asc' | 'Desc';
    jobStatus?: 'Asc' | 'Desc';
    jobType?: 'Asc' | 'Desc';
  }>({
    neighborhoodDescription: 'Asc'
  });
  const [selectedSort, setSelectedSort] = useState<string>('neighborhoodDescription');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [selectedJobs, setSelectedJobs] = useState<IJobList[]>([]);
  const [isAllSelected, setSelectedAll] = useState(false);
  const isMobile = useMediaQuery('(max-width: 960px)');

  const fetchJobs = async () => {
    try {
      setLoadingJobs(true);
      const res = await getJobsList({
        page: page + 1,
        perPage: perPage,
        jobStatus: selectedStatus,
        jobType: selectedType,
        sortBy: selectedSort,
        neighborhoodId: selectedNeighborhoodId,
        sortDirection: sortDirection[selectedSort] as string
      });
      setRecordCount(res.totalRecordCount);
      setJobs(res.records);
      // reset this checkbox if they change filters/pages so that can reselect all of them listed
      setSelectedAll(false);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error loading jobs, please refresh and try again.', {
        variant: 'error'
      });
    } finally {
      setLoadingJobs(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, selectedSort, sortDirection, filters]);

  const handleEditJob = (job: number) => {
    history.push(`/development/jobs/${job}/general-information`);
  };

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const columns = [
    {
      id: 'checkbox',
      accessor: '',
      Header: '',
      overrideWidth: 50,
      sort: false,
      Cell: ({ cell }) => {
        const job: IJobList = cell.row.original;
        const ids = selectedJobs.map(job => job.jobId);
        const isSelected = ids.includes(job.jobId);

        return (
          <FormControlLabel
            color="primary"
            control={
              <Checkbox
                checked={isSelected}
                id="jobs-checkbox"
                onChange={() => {
                  isSelected
                    ? setSelectedJobs(
                        selectedJobs.filter(selectedOrder => selectedOrder.jobId !== job.jobId)
                      )
                    : setSelectedJobs([...selectedJobs, job]);
                }}
              />
            }
            label=""
          />
        );
      }
    },
    {
      Header: 'Neighborhood',
      accessor: 'neighborhoodDescription',
      isServerSorted: selectedSort === 'neighborhoodDescription',
      isServerSortedDesc: sortDirection.neighborhoodDescription === 'Desc',
      handleClickColumn: () => handleClickColumn('neighborhoodDescription')
    },
    {
      Header: 'Lot Number',
      accessor: 'lotNumber',
      isServerSorted: selectedSort === 'lotNumber',
      isServerSortedDesc: sortDirection.lotNumber === 'Desc',
      handleClickColumn: () => handleClickColumn('lotNumber')
    },
    {
      Header: 'Job ID',
      accessor: 'code',
      isServerSorted: selectedSort === 'jobId',
      isServerSortedDesc: sortDirection.jobId === 'Desc',
      handleClickColumn: () => handleClickColumn('jobId')
    },
    {
      Header: 'Status',
      accessor: 'jobStatus',
      isServerSorted: selectedSort === 'jobStatus',
      isServerSortedDesc: sortDirection.jobStatus === 'Desc',
      handleClickColumn: () => handleClickColumn('jobStatus')
    },
    {
      Header: 'Type',
      accessor: 'jobType',
      isServerSorted: selectedSort === 'jobType',
      isServerSortedDesc: sortDirection.jobType === 'Desc',
      handleClickColumn: () => handleClickColumn('jobType')
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      sort: false,

      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IJobList } };
      }) => {
        return (
          <Box
            sx={{
              textAlign: 'right',
              display: 'flex'
            }}
          >
            <Button
              id="edit-job"
              type="button"
              color="primary"
              startIcon={<Edit />}
              onClick={() => handleEditJob(original.jobId)}
            >
              Edit
            </Button>
          </Box>
        );
      }
    }
  ];
  return (
    <>
      <PageTitleWithButton
        pageTitleProps={{
          title: 'Manage Jobs'
        }}
        button={
          <Box display="flex">
            <Button
              id="create-job"
              startIcon={<Add />}
              color="primary"
              variant="contained"
              sx={{
                marginTop: theme.spacing(1),
                width: '100%',
                [theme.breakpoints.up('sm')]: {
                  marginTop: 0,
                  width: 'auto'
                }
              }}
              onClick={() => showModal(true)}
            >
              Add Job
            </Button>
          </Box>
        }
      />
      <JobFilters
        isLoading={isLoadingJobs}
        applyFilters={(clearFilters?: boolean) => {
          if (clearFilters) {
            setFilters({
              neighborhoodId: '',
              jobStatus: '',
              jobType: ''
            });
          } else {
            setPage(0);
            setFilters({
              ...filters
            });
          }
        }}
        setPage={setPage}
        hasAppliedFilters={hasAppliedFilters}
        setHasAppliedFilters={setHasAppliedFilters}
        selectedNeighborhoodId={selectedNeighborhoodId}
        setSelectedNeighborhoodId={setSelectedNeighborhoodId}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
      />
      <JobSearch />
      <Box mt={1} mb={1}>
        <Divider />
        <Box display="flex" pl={1} pt={0.25} pb={0.25}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAllSelected}
                id="orders-checkbox"
                disabled={isLoadingJobs}
                onChange={async () => {
                  if (isAllSelected) {
                    setSelectedJobs([]);
                    setSelectedAll(false);
                  } else {
                    // grab what is currently visible on the page
                    setSelectedJobs([...selectedJobs, ...jobs]);
                    setSelectedAll(true);
                  }
                }}
              />
            }
            label=""
          />

          <Button
            id="jobs-bulk-edit"
            color="primary"
            variant="contained"
            disabled={selectedJobs.length < 2 || isLoadingJobs}
            onClick={() => {
              // passing the jobIds as location state since there could be a lot of them
              // and it seemed cleaner than using a query string
              // filter any duplicate jobIds
              const uniqueJobIds = Array.from(new Set(selectedJobs.map(job => job.jobId)));
              history.push(`/development/jobs/bulk-edit`, {
                jobIds: uniqueJobIds
              });
            }}
            startIcon={<Edit />}
            size="small"
          >
            Bulk Edit
          </Button>
        </Box>
        <Divider />
      </Box>
      <Table
        columns={columns}
        data={jobs}
        isLoading={isLoadingJobs}
        hidePagination
        mobileProps={{
          handleEdit: (job: IJobList) => handleEditJob(job.jobId),
          fields: columns
            .filter(c => c.Header)
            .map(c => ({
              name: c.Header,
              accessor: c.accessor,
              Cell: c?.Cell
            }))
        }}
        ResponsiveComponent={isMobile ? MobileTable : undefined}
      />
      {!isLoadingJobs && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
      <CreateJobModal open={isModalOpen} onClose={() => showModal(false)} />
    </>
  );
};
